import React, { useEffect, useState } from "react";
import styled from "styled-components";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import { useNavigate, useLocation } from "react-router-dom";
import { FaCalendarAlt, FaClock } from "react-icons/fa";
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react";
import { useTranslation } from "react-i18next";

const Section = styled.section`
  min-height: 100svh;
  background: #f3ecdf;
  padding: 8rem 1rem 6rem;
  text-align: left;

  @media (min-width: 768px) {
    padding: 11rem 4vw 8rem;
  }
`;

const Title = styled.h1`
  max-width: 12ch;
  margin: 0 auto;
  font-size: clamp(3rem, 8vw, 7rem);
  color: #214a3f;
  margin-bottom: 0.75rem;
  font-weight: 450;
  line-height: .88;
  letter-spacing: -.055em;
  text-wrap: balance;
`;

const SubText = styled.p`
  max-width: 660px;
  margin: 1.3rem auto 3rem;
  color: #6d6762;
  font-size: clamp(0.98rem, 3.8vw, 1.2rem);
  padding: 0;
  border-radius: 0;
  background: transparent;
  border: 0;
  box-shadow: none;
  font-family: Arial, sans-serif;

  @media (min-width: 768px) {
    padding: 1.35rem 1.4rem;
  }
`;
const Text = styled.p`
  color: #2d5b4f;
  font-size: 0.86rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  font-weight: 700;
  padding: 0.2rem 0 0.7rem;

  @media (max-width: 768px) {
    font-size: 0.8rem;
    padding: 0.2rem 0 0.65rem;
  }

  @media (max-width: 480px) {
    font-size: 0.77rem;
    padding: 0.1rem 0 0.55rem;
  }
`;

const StepTitle = styled.h3`
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  color: #224e43;
  font-size: clamp(1.8rem, 6vw, 3rem);
  margin-bottom: 0.25rem;
  line-height: 1.05;
  letter-spacing: -0.01em;
`;

const StepHeader = styled.div`
  margin-bottom: 1.5rem;
`;

const StepHint = styled.p`
  color: #6b6259;
  font-size: 0.82rem;
  margin: 0.12rem 0 0.55rem;
  font-family: Arial, sans-serif;
`;

const ActivityPill = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 0.55rem 0.8rem;
  border-radius: 0;
  border: 0;
  border-left: 2px solid #cf7046;
  background: transparent;
  color: #245147;
  font-weight: 700;
  font-size: 0.88rem;
  letter-spacing: 0.02em;
  max-width: 100%;
  margin: 0 auto;
`;

const SummaryCard = styled.div`
  background: #d9e7a8;
  border: 0;
  padding: 1.4rem;
  border-radius: 0;
  margin-bottom: 1.5rem;
  box-shadow: none;
`;

const SummaryDate = styled.strong`
  font-size: clamp(2.35rem, 12vw, 3.8rem);
  color: #1f4b40;
  display: block;
  line-height: 1;
  letter-spacing: -0.02em;
  margin-bottom: 0.65rem;
`;

const PriceLine = styled.p`
  margin-top: 0.55rem;
  color: #675f56;
  font-size: 1rem;
  letter-spacing: 0.01em;
`;

const PriceStrong = styled.span`
  color: #1f4b40;
  font-weight: 800;
`;

const TimeNotice = styled.div`
  background: transparent;
  border-top: 1px solid rgba(45, 91, 79, 0.24);
  border-bottom: 1px solid rgba(45, 91, 79, 0.24);
  border-radius: 0;
  padding: 0.82rem 0.95rem;
  margin-bottom: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.65rem;
  color: #1f4b40;
  box-shadow: none;

  svg {
    font-size: 1.2rem;
  }
`;

const TimeText = styled.span`
  font-size: clamp(2rem, 10vw, 2.8rem);
  font-weight: 800;
  letter-spacing: -0.02em;
  line-height: 1;
`;

const CalendarContainer = styled.div`
  max-width: 680px;
  margin: 0.85rem auto 0;
  background: #faf6ed;
  padding: 1rem 0.92rem 1.1rem;
  border-radius: 0;
  border-top: 1px solid rgba(45, 91, 79, 0.2);
  border-bottom: 1px solid rgba(45, 91, 79, 0.2);
  box-shadow: none;

  @media (min-width: 768px) {
    margin: 2rem auto;
    padding: 2.2rem;
    border-radius: 0;
  }
`;

const CustomCalendarWrapper = styled.div`
  display: flex;
  justify-content: center;

  .react-calendar {
    width: 100% !important;
    max-width: 750px;
    font-size: 0.9rem;
    padding: 0.6rem;
    border-radius: 0;
    background: transparent;
    border: none;

    @media (min-width: 768px) {
      font-size: 1rem;
      padding: 1.5rem;
      border-radius: 20px;
    }
  }

  .react-calendar__navigation {
    margin-bottom: 1.5rem;
  }

  .react-calendar__navigation button {
    font-size: 1.2rem;
    color: #3f6b5a;
    background: none;

    svg {
      display: block;
      width: 1.25rem;
      height: 1.25rem;
      margin: 0 auto;
    }
  }

  .react-calendar__month-view__weekdays {
    text-transform: uppercase;
    font-size: 0.75rem;
    color: #999;
    margin-bottom: 0.5rem;
  }

  .react-calendar__month-view__days {
    display: grid !important;
    grid-template-columns: repeat(7, 1fr);
    gap: 2px;

    @media (min-width: 768px) {
      gap: 6px;
    }
  }

  .react-calendar__tile {
    aspect-ratio: 1 / 1;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.95rem;
    border-radius: 0;
    background: transparent;
    color: #4a4a4a;
    transition: all 0.2s ease;
  }

  .react-calendar__tile:enabled:hover {
    color: #d67447;
    transform: scale(1.05);
  }

  .react-calendar__tile--now {
    border: 1px solid rgba(0, 0, 0, 0.12);
  }

  .react-calendar__tile.available {
    background: rgba(214, 116, 71, 0.12);
    color: #d67447;
    font-weight: 600;
  }

  .react-calendar__tile.available:hover {
    background: #d67447;
    color: white;
  }

  .react-calendar__tile--active {
    background: #3f6b5a !important;
    color: white;
    transform: scale(1.08);
  }

  .react-calendar__tile--active:hover {
    background: #355a4b !important;
  }
`;

const Button = styled.button`
  background: #173f35;
  color: #fff;
  border: none;
  padding: 0.98rem 1.3rem;
  border-radius: 0;
  font-weight: 800;
  font-size: 1rem;
  letter-spacing: 0.01em;
  cursor: pointer;
  margin-top: 0.45rem;
  box-shadow: none;

  &:hover {
    background: #cf7046;
  }

  &:disabled {
    opacity: 0.72;
    filter: grayscale(0.2);
    cursor: not-allowed;
    box-shadow: 0 8px 16px rgba(36, 79, 67, 0.18);
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 0.72rem;
  max-width: 520px;
  margin: 0.85rem auto 0.5rem;
`;

const Input = styled.input`
  padding: 0.96rem;
  min-height: 52px;
  border-radius: 0;
  border: 0;
  border-bottom: 1px solid rgba(45, 91, 79, 0.35);
  background: transparent;
  font-size: 1.02rem;
  color: #3f5f56;

  &::placeholder {
    color: #8b837a;
  }

  &:focus {
    outline: none;
    border-color: #2f6658;
    box-shadow: 0 0 0 3px rgba(45, 91, 79, 0.12);
  }
`;

const FieldError = styled.p`
  margin: -0.2rem 0 0.1rem;
  text-align: left;
  color: #b24b2f;
  font-size: 0.84rem;
  line-height: 1.35;
  padding-left: 0.35rem;
`;

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:4242";

const Reservas = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  const saved = localStorage.getItem("experienciaSeleccionada");

  const experienciaInicial =
    location.state || (saved ? JSON.parse(saved) : null);

  const [tipoSeleccionado] = useState(experienciaInicial);
  const minPersonas = tipoSeleccionado?.minPersonas || (tipoSeleccionado?.slug === "ceramica-y-vino-granollers" ? 4 : 1);
  const [step, setStep] = useState(1);
  const [fecha, setFecha] = useState(null);
  const [horario, setHorario] = useState("");
  const [availability, setAvailability] = useState({ capacity: 9, slots: {} });
  const [scheduleSlots, setScheduleSlots] = useState([]);

  const [formData, setFormData] = useState({
    nombre: "",
    email: "",
    telefono: "",
    personas: String(experienciaInicial?.minPersonas || (experienciaInicial?.slug === "ceramica-y-vino-granollers" ? 4 : 1)),
  });

  const loadAvailability = async () => {
    try {
      const [availabilityResponse, scheduleResponse] = await Promise.all([
        fetch(`${API_BASE_URL}/api/booking-availability`),
        fetch(`${API_BASE_URL}/api/voucher-slots`),
      ]);
      if (!availabilityResponse.ok || !scheduleResponse.ok) throw new Error("availability unavailable");
      const [data, schedule] = await Promise.all([availabilityResponse.json(), scheduleResponse.json()]);
      setAvailability({ capacity: data.capacity || 9, slots: data.slots || {} });
      setScheduleSlots(schedule.slots || []);
    } catch (_error) {
      // El servidor vuelve a validar antes de aceptar la reserva.
    }
  };

  useEffect(() => { loadAvailability(); }, []);

  const toDateKey = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
  const getSlotAvailability = (date, horario) => {
    const startTime = String(horario || "").match(/\d{1,2}:\d{2}/)?.[0];
    if (!date || !startTime) return { available: false, remaining: 0 };
    const occupied = availability.slots[`${toDateKey(date)}|${startTime}`];
    if (!occupied) return { available: true, remaining: availability.capacity };
    const sameActivity = occupied.slug === tipoSeleccionado?.slug;
    return { available: sameActivity && occupied.personas < availability.capacity, remaining: sameActivity ? Math.max(0, availability.capacity - occupied.personas) : 0 };
  };
  const [formError, setFormError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({
    nombre: "",
    email: "",
    telefono: "",
  });

  const nombreValido = (nombre) => /^[A-Za-zÀ-ÿ' -]{2,}$/.test(nombre.trim());
  const emailValido = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email.trim());
  const telefonoValido = (telefono) => /^\+?[0-9\s-]{9,15}$/.test(telefono.trim());

  const validateField = (field, rawValue) => {
    const value = rawValue.trim();

    if (field === "nombre") {
      if (!value) return t("reservas.errorNombreFalta");
      if (!nombreValido(value)) return t("reservas.errorNombreInvalido");
      return "";
    }

    if (field === "email") {
      if (!value) return t("reservas.errorEmailFalta");
      if (!emailValido(value)) return t("reservas.errorEmailInvalido");
      return "";
    }

    if (field === "telefono") {
      if (!value) return t("reservas.errorTelefonoFalta");
      if (!telefonoValido(value)) return t("reservas.errorTelefonoInvalido");
      return "";
    }

    return "";
  };

  const validateForm = (data) => ({
    nombre: validateField("nombre", data.nombre),
    email: validateField("email", data.email),
    telefono: validateField("telefono", data.telefono),
  });

  const hasErrors = (errors) => Object.values(errors).some(Boolean);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));

    setFieldErrors((prev) => ({
      ...prev,
      [field]: validateField(field, value),
    }));

    if (formError) setFormError("");
  };
  const isStrictlyFuture = (date) => {
    const selected = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const today = new Date();
    const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    return selected > startOfToday;
  };

  const isAvailable = (date) => {
    // Solo permitir reservas en fechas futuras.
    if (!isStrictlyFuture(date)) return false;

    return getHorarios(date).length > 0;
  };

  const isDisabledDay = ({ date }) => !isAvailable(date);

  const getHorarios = (selectedDate = fecha) => {
    if (!selectedDate) return [];
    const key = toDateKey(selectedDate);
    return scheduleSlots
      .filter((slot) => slot.date === key && slot.enabled !== false && getSlotAvailability(selectedDate, slot.time).available && getSlotAvailability(selectedDate, slot.time).remaining >= minPersonas)
      .map((slot) => slot.time)
      .sort();
  };

  const formatHorarioVisible = (value) => {
    if (!value) return "Sin horario";
    return value.includes("-") ? value : `${value}h`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const errors = validateForm(formData);
    setFieldErrors(errors);

    if (hasErrors(errors)) {
      setFormError(t("reservas.errorRevisaCampos"));
      return;
    }

    // Validación: todos los campos obligatorios deben estar presentes
    if (!fecha) {
      setFormError(t("reservas.errorFecha"));
      return;
    }

    setFormError("");

    const horarioFinal = horario;

    if (!horarioFinal) {
      setFormError(t("reservas.errorHorario"));
      return;
    }

    if (!tipoSeleccionado?.paymentLink) {
      alert(t("reservas.errorExperiencia"));
      return;
    }

    if (Number(formData.personas) < minPersonas) {
      setFormError(`Esta actividad requiere un mínimo de ${minPersonas} personas.`);
      return;
    }

    const reserva = {
      nombre: formData.nombre.trim(),
      email: formData.email.trim(),
      telefono: formData.telefono.trim(),
      personas: Number(formData.personas),
      fecha: fecha?.toLocaleDateString(),
      dateKey: toDateKey(fecha),
      horario: horarioFinal,
      tipo: tipoSeleccionado?.tipo || tipoSeleccionado?.titulo || "",
      precio: tipoSeleccionado?.precio || "",
      slug: tipoSeleccionado?.slug || "",
    };

    localStorage.setItem("reservaPendiente", JSON.stringify(reserva));

    try {
      const response = await fetch(`${API_BASE_URL}/api/create-checkout-session`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reserva }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data?.error || "No se pudo crear la sesión de pago");
      }

      if (!data?.url) {
        throw new Error("No se recibio la URL de pago");
      }

      window.location.href = data.url;
    } catch (error) {
      await loadAvailability();
      setFormError(error.message || t("reservas.errorPago"));
    }
  };

  if (!tipoSeleccionado) {
    return (
      <Section>
        <Title>{t("reservas.seleccionaExperiencia")}</Title>
        <SubText>
          {t("reservas.primeroExperiencia")}
        </SubText>
        <Button onClick={() => navigate("/experiencias-ceramica-granollers")}>
          {t("reservas.verExperiencias")}
        </Button>
      </Section>
    );
  }

  return (
    <Section>
      <Title>
        {tipoSeleccionado?.tipo || tipoSeleccionado?.titulo}
      </Title>

      <SubText>
        {tipoSeleccionado?.descripcion ||
          t("reservas.eligeExperiencia")}
      </SubText>

      <CalendarContainer>
        {step === 1 && (
          <>
            <StepHeader>
              <StepTitle>
                <FaCalendarAlt />
                {t("reservas.eligeDia")}
              </StepTitle>
              <StepHint>{t("reservas.estasPorReservar")}</StepHint>
              <ActivityPill>{tipoSeleccionado.tipo || tipoSeleccionado.titulo}</ActivityPill>
            </StepHeader>

            <CustomCalendarWrapper>
              <Calendar
                locale={i18n.language?.startsWith("ca") ? "ca-ES" : "es-ES"}
                prevLabel={<ChevronLeft aria-label="Mes anterior" />}
                nextLabel={<ChevronRight aria-label="Mes siguiente" />}
                prev2Label={<ChevronsLeft aria-label="Año anterior" />}
                next2Label={<ChevronsRight aria-label="Año siguiente" />}
                onChange={(value) => {
                  setFecha(value);
                  setHorario("");
                  setStep(2);
                }}
                value={fecha}
                tileDisabled={isDisabledDay}
                tileClassName={({ date }) =>
                  isAvailable(date) ? "available" : null
                }
              />
            </CustomCalendarWrapper>
          </>
        )}

        {step === 2 && (
          <>
            <Text>{t("reservas.unPaso")}</Text>

            <SummaryCard>
              <SummaryDate>{fecha?.toLocaleDateString()}</SummaryDate>
              <TimeNotice>
                <FaClock />
                <TimeText>{horario ? formatHorarioVisible(horario) : "Elige un horario"}</TimeText>
              </TimeNotice>
              <PriceLine>
                {t("reservas.precioLabel")} <PriceStrong>{tipoSeleccionado.precio}</PriceStrong>
              </PriceLine>
              <PriceLine>{minPersonas > 1 ? `Mínimo ${minPersonas} personas · máximo 9 por horario` : "Máximo 9 personas por horario · una sola actividad"}</PriceLine>
            </SummaryCard>

            <Form onSubmit={handleSubmit} noValidate>
              <select required value={horario} onChange={(e)=>setHorario(e.target.value)} style={{padding:".96rem",fontSize:"1rem"}}>
                <option value="">Seleccionar horario</option>
                {getHorarios(fecha).map((time)=><option key={time} value={time}>{formatHorarioVisible(time)} · {getSlotAvailability(fecha,time).remaining} plazas</option>)}
              </select>
              <select required value={formData.personas} onChange={(e)=>setFormData({...formData,personas:e.target.value})} style={{padding:".96rem",fontSize:"1rem"}}>
                {Array.from({length:Math.max(0,(horario?getSlotAvailability(fecha,horario).remaining:9)-minPersonas+1)},(_,i)=>i+minPersonas).map((count)=><option key={count} value={count}>{count} {count===1?"persona":"personas"}</option>)}
              </select>
              <Input
                type="text"
                aria-label={t("reservas.placeholderNombreCorto")}
                placeholder={t("reservas.placeholderNombreCorto")}
                value={formData.nombre}
                onChange={(e) => handleInputChange("nombre", e.target.value)}
                onBlur={(e) =>
                  setFieldErrors((prev) => ({
                    ...prev,
                    nombre: validateField("nombre", e.target.value),
                  }))
                }
                aria-invalid={!!fieldErrors.nombre}
              />
              {fieldErrors.nombre && <FieldError>{fieldErrors.nombre}</FieldError>}

              <Input
                type="email"
                aria-label={t("reservas.placeholderEmailCorto")}
                placeholder={t("reservas.placeholderEmailCorto")}
                value={formData.email}
                onChange={(e) => handleInputChange("email", e.target.value)}
                onBlur={(e) =>
                  setFieldErrors((prev) => ({
                    ...prev,
                    email: validateField("email", e.target.value),
                  }))
                }
                aria-invalid={!!fieldErrors.email}
              />
              {fieldErrors.email && <FieldError>{fieldErrors.email}</FieldError>}

              <Input
                type="tel"
                aria-label={t("reservas.placeholderTelefonoCorto")}
                placeholder={t("reservas.placeholderTelefonoCorto")}
                value={formData.telefono}
                onChange={(e) => handleInputChange("telefono", e.target.value)}
                onBlur={(e) =>
                  setFieldErrors((prev) => ({
                    ...prev,
                    telefono: validateField("telefono", e.target.value),
                  }))
                }
                aria-invalid={!!fieldErrors.telefono}
              />
              {fieldErrors.telefono && <FieldError>{fieldErrors.telefono}</FieldError>}


              {formError && (
                <p style={{ color: "#d67447", fontSize: "0.95rem" }}>{formError}</p>
              )}

              <Button type="submit">{t("reservas.continuarPago")}</Button>
            </Form>
          </>
        )}
      </CalendarContainer>
    </Section>
  );
};

export default Reservas;
